import { apiFetch, apiFetchData, withAuthHeader, type AuthRetry } from '@/shared/lib/api';
import { useAuthStore } from '@/features/auth/store';
import { toDisconnectError } from './errors';
import type {
  BalanceResponse,
  CompletedConnectResponse,
  ConnectLinkResponse,
  ConnectStatusResponse,
  DashboardLinkResponse,
  Mode,
  PayoutsListResponse,
} from './types';

const BASE = '/api/owner/stripe-connect';

function getAuthRetry(): AuthRetry {
  return {
    getToken: () => useAuthStore.getState().token,
    refreshTokens: () => useAuthStore.getState().refreshTokens(),
  };
}

function requireToken(token: string | null): string {
  if (!token) throw new Error('No token');
  return token;
}

/**
 * Account-level (owner JWT) Stripe Connect API.
 * No restaurantId on any call — scope is the authenticated owner.
 */
export const stripeConnectApi = {
  createConnectLink(token: string | null, mode: Mode): Promise<ConnectLinkResponse> {
    const t = requireToken(token);
    return apiFetchData<ConnectLinkResponse>(
      `${BASE}/link`,
      { method: 'POST', headers: withAuthHeader(undefined, t), body: { mode } },
      getAuthRetry(),
    );
  },

  completeConnect(token: string | null, mode: Mode): Promise<CompletedConnectResponse> {
    const t = requireToken(token);
    return apiFetchData<CompletedConnectResponse>(
      `${BASE}/complete`,
      { method: 'POST', headers: withAuthHeader(undefined, t), body: { mode } },
      getAuthRetry(),
    );
  },

  getConnectStatus(token: string | null): Promise<ConnectStatusResponse> {
    const t = requireToken(token);
    return apiFetchData<ConnectStatusResponse>(
      `${BASE}/status`,
      { method: 'GET', headers: withAuthHeader(undefined, t) },
      getAuthRetry(),
    );
  },

  createDashboardLink(token: string | null, mode: Mode): Promise<DashboardLinkResponse> {
    const t = requireToken(token);
    return apiFetchData<DashboardLinkResponse>(
      `${BASE}/dashboard-link`,
      { method: 'POST', headers: withAuthHeader(undefined, t), body: { mode } },
      getAuthRetry(),
    );
  },

  async disconnect(token: string | null, mode: Mode): Promise<void> {
    const t = requireToken(token);
    try {
      await apiFetch<void>(
        `${BASE}?mode=${encodeURIComponent(mode)}`,
        { method: 'DELETE', headers: withAuthHeader(undefined, t) },
        getAuthRetry(),
      );
    } catch (err) {
      const blocked = toDisconnectError(err);
      if (blocked) throw blocked;
      throw err;
    }
  },

  getPayouts(token: string | null, mode: Mode): Promise<PayoutsListResponse> {
    const t = requireToken(token);
    return apiFetchData<PayoutsListResponse>(
      `${BASE}/payouts?mode=${encodeURIComponent(mode)}`,
      { method: 'GET', headers: withAuthHeader(undefined, t) },
      getAuthRetry(),
    );
  },

  getBalance(token: string | null, mode: Mode): Promise<BalanceResponse> {
    const t = requireToken(token);
    return apiFetchData<BalanceResponse>(
      `${BASE}/balance?mode=${encodeURIComponent(mode)}`,
      { method: 'GET', headers: withAuthHeader(undefined, t) },
      getAuthRetry(),
    );
  },
};
